/*
 * 版权所有 (c) 华为技术有限公司 2024
 * Copyright (c) Huawei Technologies Co., Ltd. 2024. All rights reserved.
 */
import { type OnDestroy, type OnInit, Component, HostListener, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import { CoreEventServiceKeyE, EventService, LayoutStyleService, LazyloadService, PromptService } from '@dm/core';
import { SYSTEM_VSTORE_ID, SYSTEM_VSTORE_NAME } from 'app/common/constants';
import { EventServiceKeyE } from 'app/common/constants/event-service-key';
import { SIMPLE_MODE_PATH } from 'app/common/constants/simple-mode.const';
import { ToolService } from 'app/common/services';
import { ShardSwitch } from 'app/config/switch';
import { type LunWorkLoadSimpleModeTypeE } from 'app/feature/service/block-service/lun-group/luns.const';
import { CreateFileShareComponent } from 'app/feature/service/file-service/file-system/create-file-share/create-file-share.component';
import { ShareEntryE } from 'app/feature/service/file-service/file-system/file-system-str.service';
import { type Subscription } from 'rxjs';

import { ExportSimpleComponent } from '../header-settings/export-simple/export-simple.component';
import { SmartAssistantAlarmsComponent } from './alarms/assistant-alarms.component';
import { SmartAssistantFileShareComponent } from './file-share/file-share.component';
import { SmartAssistantProtectionComponent } from './object-protection/object-protection.component';
import { SmartAssistantReportComponent } from './report/assistant-report.component';
import { type GuideConfig } from './smart-assistant';
import { ENTRY_CHECK_LIST, GuideCreateOptions, GuideEventKey, GuideStorageKey, MAX_HEIGHT, PermissionCheckObjE, SHOW_HOME_ENTRY, SmartAssistantEntryE } from './smart-assistant.const';
import { SmartAssistantService } from './smart-assistant.service';
import { SmartAssistantStrService } from './smart-assistant-str.service';

@Component({
    selector: 'app-smart-assistant',
    templateUrl: './smart-assistant.component.html',
    styleUrls: ['./smart-assistant.component.scss'],
    host: {
        '[style.top]': 'smartAssistantTop',
        '[style.height]': 'smartAssistantHeight',
        '[style.marginTop]': 'smartAssistantMarginTop',
    },
})
export class SmartAssistantComponent implements OnInit, OnDestroy {
    /**
     * 文件共享
     */
    @ViewChild(SmartAssistantFileShareComponent) fileShare: SmartAssistantFileShareComponent;
    /**
     * 数据保护
     */
    @ViewChild(SmartAssistantProtectionComponent) protection: SmartAssistantProtectionComponent;
    /**
     * 告警
     */
    @ViewChild(SmartAssistantAlarmsComponent) alarmEl: SmartAssistantAlarmsComponent;
    /**
     * 报告
     */
    @ViewChild(SmartAssistantReportComponent) reportEl: SmartAssistantReportComponent;
    /**
     * 极简模式日志导出组件
     */
    @ViewChild('exportSimple') exportSimple: ExportSimpleComponent;

    /**
     * 国际化
     */
    i18n = this.str.i18n;
    /**
     * 发放存储资源页面
     */
    showStorageResGuide = false;
    /**
     * 是否有未完成的进程
     */
    hasUnfinishedProcess = false;
    /**
     * 未完成进程-当前步骤
     */
    processStep: number;
    /**
     * 未完成进程-应用类型
     */
    processType: string;
    /**
     * 未完成进程-开始时间
     */
    processStartTime: string;
    /**
     * 显示忙等
     */
    showLoading = true;
    /**
     * 不同页面展示的智能助手内容不同
     */
    entry: SmartAssistantEntryE;
    SmartAssistantEntryE = SmartAssistantEntryE;
    SHOW_HOME_ENTRY = SHOW_HOME_ENTRY;
    ENTRY_CHECK_LIST = ENTRY_CHECK_LIST;
    /**
     * 监听智能助手配置变化
     */
    assistantConfigSub$: Subscription;
    /**
     * 是否有license或权限
     */
    hasGuidePermission: boolean;
    hasFsPermission: boolean;
    /**
     * 常量
     */
    ShardSwitch = ShardSwitch;
    MAX_HEIGHT = MAX_HEIGHT;
    /**
     * 文件服务/数据保护等模块，展开/折叠状态
     */
    fileShareExpand = false;
    protectionExpand = false;
    alarmExpand = false;
    /**
     * top值（受header上方提示影响）
     */
    smartAssistantTop: string;
    /**
     * 高度
     */
    smartAssistantHeight: string;
    /**
     * marginTop
     */
    smartAssistantMarginTop: string;
    /**
     * 展示日志导出
     */
    showExportLog = false;

    supportSmartThin = this.toolService.supportSmartThinLicense();

    topTipContent = this.supportSmartThin ? this.i18n.firstTip : this.i18n.noSanFirstTip;

    /**
     * 展示首页的智能助手（进入告警/报告页面，数据为空时展示）
     */
    get showHome(): boolean {
        if (this.entry === SmartAssistantEntryE.AlarmAndEvent) {
            return this.alarmEl?.data.recent.length === 0;
        }

        if (this.entry === SmartAssistantEntryE.Report) {
            return this.reportEl?.reportDatas.length === 0;
        }

        return false;
    }

    constructor(
        public str: SmartAssistantStrService,
        private service: SmartAssistantService,
        private promptService: PromptService,
        private router: Router,
        private eventService: EventService,
        private lazyloadService: LazyloadService,
        private toolService: ToolService,
        private layoutStyleService: LayoutStyleService,
    ) { }

    /**
     * 滚动时更新top值
     */
    @HostListener('window:scroll')
    onScroll(): void {
        // 小屏出现滚动条，需调整marginTop值，避免出现空白区域
        this.smartAssistantMarginTop = `${-document.documentElement.scrollTop}px`;
    }

    ngOnInit(): void {
        this.checkPermission();
        this.adjustTopAndHeight();

        if (!this.assistantConfigSub$) {
            this.assistantConfigSub$ = this.eventService.subscribe(GuideEventKey.assistantConfigChange,
                (params: GuideConfig) => {
                    this.showExportLog = params.showExportLog;

                    if (this.entry !== params.entry) {
                        this.entry = params.entry;

                        if (this.entry !== SmartAssistantEntryE.Block) {
                            this.service.setGuideConfig(GuideStorageKey.startGuiding, false, false);
                            this.backToHome();
                        }
                    }
                },
            );
        }
        this.entry = this.service.getGuideConfig(GuideStorageKey.entry);
        this.checkProcess();
    }

    /**
     * 根据场景调整top值和高度
     */
    adjustTopAndHeight(): void {
        this.upDateTopAndHeight();
        this.eventService.subscribe(CoreEventServiceKeyE.CHECK_TIPS_OVER_HEADER, () => {
            this.upDateTopAndHeight();
        });
    }

    /**
     * 更新top值和高度
     */
    upDateTopAndHeight(): void {
        this.smartAssistantTop = this.layoutStyleService.getHeightOfTipsOverHeader();
        this.smartAssistantHeight = `calc(${document.body.offsetHeight}px - ${this.smartAssistantTop})`;
    }

    /**
     * 检查是否有License或某个特性的权限
     */
    checkPermission(): void {
        this.hasFsPermission = this.service.checkPermission(PermissionCheckObjE.Fs);
        this.hasGuidePermission = this.service.checkPermission(PermissionCheckObjE.LunAndHost);
    }

    /**
     * 检查是否有未完成的进程，并获取类型和开始时间
     */
    checkProcess(): void {
        this.showLoading = true;
        this.service.checkFinished().then(
            (isFinished?: boolean) => {
                this.processStep = this.service.getGuideConfig(GuideStorageKey.step);
                this.service.setGuideConfig(GuideStorageKey.isFinished, isFinished);

                // 没返回isFinished，表明还未开始引导
                this.hasUnfinishedProcess = !(isFinished ?? true);
                this.setOrClearInfo(this.hasUnfinishedProcess);
            },
            () => {
                // 主机可能被删除
                this.processStep = this.service.getGuideConfig(GuideStorageKey.step);
                this.service.setGuideConfig(GuideStorageKey.isFinished, false);
                this.hasUnfinishedProcess = true;
                this.setOrClearInfo(this.hasUnfinishedProcess);
            },
        );
    }

    /**
     * 设置未完成进程的相关信息或清空上一次Guide记录
     */
    setOrClearInfo(unfinished: boolean): void {
        if (unfinished) {
            const { guideOperateType, startTime } = this.service.getGuideInfo();
            this.processType = this.str.getOperateTypeLabel(guideOperateType);
            this.processStartTime = startTime;
        } else {
            this.service.clearGuideProcess();
        }
        this.showLoading = false;
    }

    /**
     * 结束未完成的进程
     */
    endProcess(): void {
        this.promptService.info({
            id: 'prompt-smart-assistant-delete',
            title: this.i18n.endTaskTitle,
            content: this.i18n.finishedProcessTip,
            onOk: () => {
                this.promptService.close();
                this.hasUnfinishedProcess = false;
                this.service.clearGuideProcess();
            },
        });
    }

    /**
     * 跳转到引导页（传入type表明重新开始）
     * @param type 应用类型
     */
    jumpToGuide(type?: LunWorkLoadSimpleModeTypeE): void {
        this.service.setGuideConfig(GuideStorageKey.showPrimaryBtn, false, false);
        const jumpFunc = (): void => {
            this.showStorageResGuide = true;
            this.updateRouterPath();

            if (type) {
                if (this.hasUnfinishedProcess) {
                    this.hasUnfinishedProcess = false;
                    this.service.clearGuideProcess();
                }
                this.service.setGuideConfig(GuideStorageKey.guideOperateType, type, false);
                this.service.setGuideConfig(GuideStorageKey.isFinished, false);
            } else {
                // 如果没传type，说明是“继续进程”，需要将指导状态设置为开启
                this.service.setGuideConfig(GuideStorageKey.startGuiding, true);
            }
        };

        if (this.hasUnfinishedProcess && type) {
            this.promptService.info({
                id: 'prompt-smart-assistant-jumpToGuide',
                title: this.i18n.startNewTaskTitle,
                content: this.i18n.unfinishedProcessTip,
                onOk: (): void => {
                    this.promptService.close();
                    jumpFunc();
                },
            });
        } else {
            jumpFunc();
        }
    }

    /**
     * 创建文件系统
     */
    createFileSystem(): void {
        this.eventService.emit(EventServiceKeyE.ClickAssistantCreateBtn);
        this.lazyloadService.appendComponent(CreateFileShareComponent, {
            shareEntry: ShareEntryE.SmartAssistant,
            vstore: {
                label: SYSTEM_VSTORE_NAME,
                value: SYSTEM_VSTORE_ID,
            },
        });
    }

    /**
     * 点击后默认跳转到LUN页面
     */
    updateRouterPath(): void {
        let targetPath = SIMPLE_MODE_PATH.LUN;
        const guideStep = this.service.getGuideConfig(GuideStorageKey.step) ?? null;
        if (guideStep !== null &&
            [GuideCreateOptions.CreateHost, GuideCreateOptions.CreateHost, GuideCreateOptions.MapLunToHost].includes(guideStep)
        ) {
            // 如果之前进行过引导并且不处于“创建LUN”步骤，则跳转到主机页面
            targetPath = SIMPLE_MODE_PATH.HOST;
        }
        this.router.navigate([targetPath]);
    }

    /**
     * 回到智能助手起始页
     */
    backToHome(): void {
        this.showStorageResGuide = false;
        this.checkProcess();
    }

    /**
     * 隐藏智能助手页
     */
    hide(): void {
        // 如果入口是日志导出，则调用日志导出里的close
        if (this.showExportLog) {
            this.exportSimple?.onClose();
            return;
        }

        // 隐藏智能助手里的日志导出
        this.service.setGuideConfig(GuideStorageKey.showExportLog, false, false);
        this.service.setGuideConfig(GuideStorageKey.showSmartAssistant, false);
        this.service.initStatus();
    }

    /**
     * 切换展开/折叠
     */
    changeExpand(objType: SmartAssistantEntryE): void {
        if (objType === SmartAssistantEntryE.FileShare) {
            this.fileShareExpand = !this.fileShareExpand;
            return;
        }

        if (objType === SmartAssistantEntryE.AlarmAndEvent) {
            this.alarmExpand = !this.alarmExpand;
            return;
        }

        this.protectionExpand = !this.protectionExpand;
    }

    ngOnDestroy(): void {
        this.service.setGuideConfig(GuideStorageKey.showSmartAssistant, false, false);
        this.service.setGuideConfig(GuideStorageKey.startGuiding, false);
        if (this.assistantConfigSub$) {
            this.assistantConfigSub$.unsubscribe();
        }
    }
}
